/* eslint-disable camelcase */
import React, { useCallback, useState } from 'react'
import { navigate } from 'gatsby'
import { useLDClient } from 'gatsby-plugin-launchdarkly'
import Select, {
  components,
  DropdownIndicatorProps,
  GroupBase
} from 'react-select'
import { useQueryParams, StringParam } from 'use-query-params'
import { faSquare } from '@fortawesome/free-regular-svg-icons/faSquare'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputBase,
  styled,
  SxProps,
  Typography,
  useTheme
} from '@mui/material'

import { DynamicInputFormSingleField } from './DynamicInputFormSingleField'
import { Icon } from './Icon'
import { PortableText } from './PortableText' // eslint-disable-line import/no-cycle

import { isEmailValid, isEmailValidForEnterpriseRequests } from '~/utils/forms'

const DropdownIndicator = (
  props: JSX.IntrinsicAttributes &
    DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>
) => (
  <components.DropdownIndicator {...props}>
    <Icon icon='chevron-down' color='text.secondary' sx={{ width: 14 }} />
  </components.DropdownIndicator>
)

declare global {
  interface Window {
    rudderanalytics: Record<string, any> // eslint-disable-line
    [key: string]: any // eslint-disable-line
  }
}

export type FormProps = {
  formProps: {
    form_id: string
    customFormId: string
    section_id?: string
    ref_form_input: {
      fields: {
        _key: string
        field_dropdown_values: string[]
        field_block_label: object
        additional_validation: string
        field_label: string
        field_name: string
        field_placeholder: string
        field_type: string
        is_required: string
        show_label: string
      }[]
      submit_button_text: string
      title: string
      tracking_field_name: string
    }
    additional_track_calls_new: {
      analytic_sdk_name: string
      analytic_function_name: string
      function_arguments: { argument_type: string; argument_value: string }[]
    }[]
    usebasin_endpoint: string
    on_success_message: string
    on_success_navigate_url?: string
  }
  singleFieldForm?: boolean
  errorAfterButton?: boolean
  getWhitepaper?: (value: boolean) => void
  onFormSubmitted?: (value: boolean) => void
  sx?: SxProps
}

const Input = styled(InputBase)`
  ${({ theme }) => `
    width: 100%;
    
    input, textarea {
      background-color: ${theme.palette.background.default};
      border: 1px solid ${theme.palette.border.main};
      border-radius: ${theme.spacing(1)};
      padding: ${theme.spacing(1.5)} ${theme.spacing(2)};

      ::placeholder {
        color: ${theme.palette.text.secondary};
        opacity: 1;
      }
    }
  `}
`

export const DynamicInputForm = ({
  formProps,
  singleFieldForm,
  errorAfterButton,
  getWhitepaper,
  onFormSubmitted,
  sx
}: FormProps) => {
  const ldClient = useLDClient()
  const { palette } = useTheme()

  const requestedDemo = formProps?.form_id?.includes('demo') ? true : null
  const signedUp = formProps?.form_id?.includes('signup') ? true : null

  const formDefinition = formProps.ref_form_input

  let formStructure: { [key: string]: string | boolean } = {}
  formDefinition?.fields?.forEach(field => {
    formStructure = {
      ...formStructure,
      [field.field_name]: field.field_type === 'checkbox' ? false : ''
    }
  })

  const [formData, setFormData] = useState(formStructure)
  const [formError, setFormErrors] = useState(formStructure)
  const [isLoading, setIsLoading] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)

  const isFieldInvalid = useCallback(
    (field: string, value: string | boolean) => {
      const formField = formDefinition?.fields.find(x => x.field_name === field)

      if (!formField) return ''
      if (typeof value === 'boolean') return ''

      if (formField.additional_validation === 'email') {
        if (formField.is_required && !value.trim()) {
          return 'This field is required.'
        }
        if (!value.trim() || !isEmailValid(value)) {
          return 'Email is invalid'
        }

        return isEmailValidForEnterpriseRequests(value)
      }

      return formField.is_required && !value.trim()
        ? 'This field is required.'
        : ''
    },
    [formDefinition.fields]
  )

  const isFormInvalid = useCallback(() => {
    let ret = false
    let errObj = formError

    Object.keys(formData).forEach(key => {
      const message = isFieldInvalid(key, formData[key])
      errObj = { ...errObj, [key]: message }

      if (message) ret = true
    })

    setFormErrors(errObj)
    return ret
  }, [formData, formError, isFieldInvalid])

  const handleBlur =
    (field: string) => (e: React.FocusEvent<HTMLInputElement>) => {
      setFormErrors({
        ...formError,
        [field]: isFieldInvalid(field, e.target.value)
      })
    }

  const handleChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: e.target.value })
    }

  const handleCheck = (field: string) => () => {
    setFormData({ ...formData, [field]: !formData[field] })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelect = (field: string) => (e: any) => {
    let value = ''
    if (Array.isArray(e)) {
      value = e?.map(x => x.value).join(', ')
    } else {
      value = e.value
    }

    setFormData({ ...formData, [field]: value })
  }

  const [queryParams] = useQueryParams({
    utm_source: StringParam,
    utm_medium: StringParam,
    utm_campaign: StringParam,
    utm_content: StringParam,
    utm_term: StringParam,
    utm_referrer: StringParam,
    raid: StringParam,
    gclid: StringParam,
    estimated_event_volume: StringParam
  })

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    if (isLoading) return
    if (isFormInvalid()) return

    if (typeof getWhitepaper !== 'undefined') {
      getWhitepaper(true)
    }

    setIsLoading(true)

    const commonPayload = {
      form_id: formProps?.form_id,
      conversion_page: document.title,
      branch: process.env.GATSBY_BRANCH,
      timezone: {
        name: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      utm_source: queryParams.utm_source,
      utm_medium: queryParams.utm_medium,
      utm_campaign: queryParams.utm_campaign,
      utm_content: queryParams.utm_content,
      utm_term: queryParams.utm_term,
      raid: queryParams.raid,
      gclid: queryParams.gclid,
      utm_referrer: queryParams.utm_referrer
    }

    if (window.rudderanalytics) {
      window.rudderanalytics.identify(
        formData[formDefinition.tracking_field_name],
        {
          ...formData,
          ...commonPayload,
          estimated_event_volume: queryParams.estimated_event_volume
        },
        {
          integrations: {
            Salesforce: true
          }
        }
      )

      window.rudderanalytics.track(
        'form_submit',
        {
          ...formData,
          ...commonPayload,
          page: document.title,
          page_URL: window.location.href,
          label: formProps?.form_id,
          category: formProps?.section_id
        },
        {
          traits: {
            ...formData,
            form_id: formProps?.form_id,
            conversion_page: document.title
          }
        }
      )
    }

    formProps?.additional_track_calls_new?.forEach(item => {
      const args: string[] = []

      item.function_arguments.forEach(arg => {
        if (arg.argument_type === 'json') {
          args.push(JSON.parse(arg.argument_value))
        } else {
          args.push(arg.argument_value)
        }
      })

      if (!window[item.analytic_sdk_name]) return

      if (item.analytic_function_name && item.analytic_function_name !== '') {
        window[item.analytic_sdk_name][item.analytic_function_name](...args)
      } else {
        window[item.analytic_sdk_name](...args)
      }
    })

    try {
      __adroll.record_user({ adroll_segments: '0783e5a8' })
    } catch (err) {}

    if (ldClient) {
      ldClient.identify({
        key: formData[formDefinition.tracking_field_name]?.toString(),
        ...formData,
        anonymous: false,
        custom: {
          ...formData,
          ...commonPayload,
          ...(!!requestedDemo && { requestedDemo }),
          ...(!!signedUp && { signedUp })
        }
      })

      ldClient.track(formProps?.form_id)
    }

    const response = await fetch(formProps?.usebasin_endpoint, {
      method: 'post',
      body: JSON.stringify({
        ...formData,
        ...commonPayload,
        page_URL: window.location.href
      }),
      headers: { 'Content-Type': 'application/json' }
    })

    if (
      formProps?.on_success_navigate_url &&
      (response.ok || response.statusText === 'OK')
    ) {
      if (formProps?.on_success_navigate_url.startsWith('http')) {
        window.open(formProps?.on_success_navigate_url, '_self')
      } else {
        navigate(
          (formProps?.on_success_navigate_url.startsWith('/') ? '' : '/') +
            (formProps?.on_success_navigate_url || '')
        )
      }
    }

    if (!formProps?.on_success_navigate_url) {
      setFormSubmitted(true)
      if (typeof onFormSubmitted !== 'undefined') {
        onFormSubmitted(true)
      }
    }
  }

  if (singleFieldForm) {
    return (
      <DynamicInputFormSingleField
        formProps={formProps}
        errorAfterButton={errorAfterButton}
        formData={formData}
        formError={formError}
        formSubmitted={formSubmitted}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        sx={sx}
      />
    )
  }

  return (
    <Box
      component='form'
      data-test={formProps?.form_id}
      id={formProps?.form_id}
      onSubmit={handleSubmit}
      sx={{
        bgcolor: 'rgba(177, 190, 203, 0.15)',
        border: 1,
        borderColor: 'border.main',
        borderRadius: { xs: 4, laptop: 6 },
        px: { xs: 3, laptop: 8 },
        py: { xs: 5, laptop: 8 },
        /* Can be removed after tailwind is removed */
        button: { bgcolor: 'primary.main' },
        ...sx
      }}>
      {formDefinition?.fields?.map((field, index) => (
        <Box key={field._key} sx={{ mt: index > 0 ? 3 : 0 }}>
          {field.show_label && field.field_type !== 'checkbox' && (
            <Typography
              className='field-label'
              sx={{ fontSize: 18, fontWeight: 500 }}>
              {field.field_label} {field.is_required && <span>*</span>}
            </Typography>
          )}

          {['input', 'textarea'].includes(field.field_type) && (
            <Input
              multiline={field.field_type === 'textarea'}
              type='text'
              name={field.field_name}
              placeholder={field.field_placeholder}
              rows={4}
              value={formData[field.field_name]}
              onBlur={handleBlur(field.field_name)}
              onChange={handleChange(field.field_name)}
              sx={{
                mt: 0.5,
                input: {
                  borderColor: formError[field.field_name]
                    ? 'error.main'
                    : 'border.main',
                  '&::placeholder': {
                    fontSize: 16
                  },

                  /* Can be removed after tailwind is removed */
                  ':-webkit-autofill': {
                    caretColor: '#070E1F',
                    WebkitBoxShadow: '0 0 0 25px #fff inset',
                    WebkitTextFillColor: '#070E1F'
                  }
                }
              }}
            />
          )}

          {['dropdown', 'multidropdown'].includes(field.field_type) && (
            <Box
              component={Select}
              isMulti={field.field_type === 'multidropdown'}
              components={{ DropdownIndicator }}
              options={field?.field_dropdown_values?.map((x: string) => ({
                value: x,
                label: x
              }))}
              placeholder={field.field_placeholder}
              onChange={handleSelect(field.field_name)}
              classNamePrefix='form-select'
              sx={{
                mt: 0.5,
                '.form-select__control': {
                  borderColor: formError[field.field_name]
                    ? 'error.main'
                    : 'border.main',
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: formError[field.field_name]
                      ? 'error.main'
                      : 'border.main'
                  }
                },
                '.form-select__value-container': { px: 2, py: 1 },
                '.form-select__placeholder': {
                  color: 'text.secondary',
                  m: 0,
                },
                '.form-select__single-value': { color: 'text.primary', m: 0 },
                '.form-select__indicator': { px: 2.5 }
              }}
            />
          )}

          {field.field_type === 'checkbox' && (
            <FormControlLabel
              control={
                <Checkbox
                  icon={
                    <Icon icon={faSquare} color={palette.background.default} />
                  }
                  checkedIcon={
                    <Icon
                      icon='check-square'
                      color={palette.background.default}
                    />
                  }
                  checked={!!formData[field.field_name]}
                  onChange={handleCheck(field.field_name)}
                  sx={{
                    width: 14,
                    mr: 1.5,
                    p: 0
                  }}
                />
              }
              label={
                field.field_block_label ? (
                  <PortableText blocks={field?.field_block_label} />
                ) : (
                  field.field_label
                )
              }
              sx={{
                m: 0,
                '.MuiFormControlLabel-label': {
                  fontSize: 18
                }
              }}
            />
          )}

          {formError[field.field_name] && (
            <Typography
              variant='subtitle2'
              data-test='error-message'
              sx={{
                color: 'error.main',
                mt: 0.5
              }}>
              {formError[field.field_name]}
            </Typography>
          )}
        </Box>
      ))}

      <Button
        variant='contained'
        type='submit'
        disabled={isLoading}
        sx={{
          width: '100%',
          bgcolor: 'primary.main',
          borderRadius: 2,
          color: 'background.default',
          fontWeight: 700,
          mt: { xs: 4, laptop: 5 },
          py: 1.5,
          '&.Mui-disabled': {
            bgcolor: 'primary.main',
            color: 'background.default',
            opacity: 0.7
          }
        }}>
        {formDefinition && formDefinition.submit_button_text}
      </Button>
    </Box>
  )
}
